import { ReactElement, useState, useEffect } from 'react';
import { Chip, Grid, Stack, Typography } from '@mui/material';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { AppContainer } from '../../components/AppContainer/AppContainer';
import { Header } from '../../components/Header/Header';
import {
  useSaveProjectMutation,
  useGetProjectQuery,
  useSaveStandardMutation,
  useSaveCalculationMutation,
  useDeleteCalculationMutation,
  useSaveZoneDetailsMutation,
  useGetUserStandardTemplateQuery,
} from 'redux/services/projects';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import AppBodyContainer from 'components/AppBodyContainer/AppBodyContainer';
import CenteredComponentView from 'components/CenteredComponentView/CenteredComponentView';
import { useTranslation } from 'react-i18next';
import {
  Calculation,
  CalculationDetails,
  EditProject,
  NewProject,
  Project,
  Standard,
  Standards,
  SupportWidth,
  CalculationDialogMode,
  ZoneDetails,
  UserStandard,
} from 'types/domainTypes';
import { Panel } from 'components/Panel/Panel';
import { ShrinkToIconButton } from 'components/ShrinkToIconButton/ShrinkToIconButton';
import { EditOutlined } from '@mui/icons-material';
import { StatusLabel } from '../Projects/StatusLabel';
import { NewProjectDialog } from 'pages/Projects/NewProjectDialog';
import { useGetStandardsQuery } from '../../redux/services/standards';
import { StandardDialog } from './ProjectStandardsDialog';
import { LabelledText } from 'components/LabelledText/LabelledText';
import { CalculationDialog } from './ProjectCalculationsDialog';
import { AddOnHoverPanel } from 'components/Panel/AddOnHoverPanel';
import { ProjectCalculationsDetailsView } from './ProjectCalculationsDetailsView';
import { SkeletonPage } from '../../components/SkeletonPage/SkeletonPage';
import { useGetSupportWidthsQuery } from 'redux/services/supportWidths';
import { breadcrumbHeight } from 'components/AppBreadcrumbs/AppBreadcrumbs';
import { duplicateCalculationData } from 'common/duplicateMethods';
import { useAppSelector } from 'redux/hooks';
import _ from 'lodash';

interface ProjectDetailsViewProps {
  project: Project;
  onEditProject: (project: NewProject) => void;
  error?: FetchBaseQueryError | SerializedError;
  isNewProjectDialogOpen: boolean;
  setIsNewProjectDialogOpen: (isDialogOpen: boolean) => void;
  supportWidthOptions?: SupportWidth[];
}

function ProjectDetailsView({
  project,
  onEditProject,
  error,
  isNewProjectDialogOpen,
  setIsNewProjectDialogOpen,
}: ProjectDetailsViewProps): ReactElement {
  const { t } = useTranslation();

  const title = t('projects-page.project-dialog.edit-project-header');

  const panelButton = (
    <ShrinkToIconButton
      buttonText={t('project-details-page.edit-project-button')}
      icon={<EditOutlined />}
      onClick={() => setIsNewProjectDialogOpen(true)}
    />
  );
  return (
    <Panel
      panelTitle={t('project-details-page.label-project-details')}
      panelSummary={t('common.summary-format', {
        createdBy: project.createdByName ? project.createdByName : project.createdBy,
        modifiedOn: project.modifiedOn,
        modifiedBy: project.modifiedByName ? project.modifiedByName : project.modifiedBy,
      })}
      panelButton={panelButton}
    >
      {isNewProjectDialogOpen && (
        <NewProjectDialog
          title={title}
          onClose={() => setIsNewProjectDialogOpen(false)}
          onSaveProject={onEditProject}
          error={error}
          project={project}
        />
      )}

      <Grid item xs={4}>
        <Stack spacing={2}>
          <LabelledText label="project-details-page.label-project-engineer" value={project.engineer} />
          <LabelledText label="project-details-page.label-project-client" value={project.client} />
          <LabelledText label="project-details-page.label-project-reference" value={project.reference} />
        </Stack>
      </Grid>
      <Grid item xs={4} alignItems="start" display={project.description ? 'inherit' : 'none'}>
        <LabelledText label="project-details-page.label-project-description" value={project.description} showNewline />
      </Grid>
      <Grid item xs={4}>
        <LabelledText label="project-details-page.label-project-notes" value={project.notes} showNewline />
      </Grid>
    </Panel>
  );
}

export function StandardDetailsViewBody({ standard }: { standard: Standard }): ReactElement {
  return (
    <>
      <Grid item xs={4}>
        <Stack spacing={2}>
          <LabelledText
            label="project-details-page.label-project-support-width"
            value={`${standard.supportWidth?.end}/${standard.supportWidth?.intermediate}`}
          />
          <LabelledText label="project-details-page.standard-dialog.label-country-field" value={standard.country} />
          <LabelledText label="project-details-page.standard-dialog.label-standard" value={standard.reference} />
          <LabelledText label="project-details-page.standard-dialog.label-annexes-field" value={standard.annexe} />
        </Stack>
      </Grid>
      <Grid item xs={4}>
        <Stack spacing={2}>
          <LabelledText label="project-details-page.standard-dialog.label-construction-class-field" value={standard.constructionClass} />
          <LabelledText
            label="project-details-page.standard-dialog.label-params-coefficients-field"
            value={standard.paramsAndCoefficients}
          />
          <LabelledText label="project-details-page.standard-dialog.label-load-factors-field" value={standard.loadFactors} />
        </Stack>
      </Grid>
      <Grid item xs={4}>
        <Stack spacing={2}>
          <LabelledText
            label="project-details-page.standard-dialog.label-deflection-limit-roof"
            value={standard.deflectionLimitRoof?.name}
            isShowCustomizedTag={standard.deflectionLimitRoof?.customized}
          />
          <LabelledText
            label="project-details-page.standard-dialog.label-deflection-limit-wall"
            value={standard.deflectionLimitWall?.name}
            isShowCustomizedTag={standard.deflectionLimitWall?.customized}
          />
        </Stack>
      </Grid>
    </>
  );
}

interface ProjectStandardsDetailsViewProps {
  standard?: Standard;
  onAddStandardClick: () => void;
  onEditStandardsDetails: () => void;
  shouldShowLabel?: boolean;
}

function ProjectStandardsDetailsView({
  standard,
  onAddStandardClick,
  onEditStandardsDetails,
  shouldShowLabel,
}: ProjectStandardsDetailsViewProps): ReactElement {
  const { t } = useTranslation();

  if (!standard) {
    return (
      <AddOnHoverPanel
        onClick={onAddStandardClick}
        titleText={t('project-details-page.label-add-standard')}
        captionText={t('project-details-page.label-add-standard-caption')}
      />
    );
  }

  const panelButton = (
    <ShrinkToIconButton
      buttonText={t('project-details-page.edit-standard-button')}
      icon={<EditOutlined />}
      onClick={onEditStandardsDetails}
    />
  );

  return (
    <Panel
      panelTitle={t('project-details-page.label-project-standard')}
      panelSummary={t('common.summary-format', {
        createdBy: standard.createdByName ? standard.createdByName : standard.createdBy,
        modifiedOn: standard.modifiedOn,
        modifiedBy: standard.modifiedByName ? standard.modifiedByName : standard.modifiedBy,
      })}
      panelButton={panelButton}
      panelChip={
        shouldShowLabel ? (
          <Chip
            label={t('project-details-page.label-standard-template')}
            size="small"
            sx={{ px: 2, ml: 2, mb: 1, '& span': { fontSize: 14 } }}
          />
        ) : (
          <></>
        )
      }
    >
      <StandardDetailsViewBody standard={standard} />
    </Panel>
  );
}

function ProjectPage(): ReactElement {
  const { t } = useTranslation();
  const { projectId } = useParams<'projectId'>();
  const navigate = useNavigate();
  const {
    data: project,
    error: getProjectError,
    isLoading: isGetProjectLoading,
    refetch,
  } = useGetProjectQuery(projectId || '', { refetchOnMountOrArgChange: true });
  const [editProject, { data: updatedProject, error: editProjectError }] = useSaveProjectMutation();
  const [saveStandard, { error: saveStandardError }] = useSaveStandardMutation();
  const [saveCalculation, { data: savedCalculation, error: saveCalculationError }] = useSaveCalculationMutation();
  const [duplicateCalculation, { data: duplicatedCalculation, error: duplicateCalculationError }] = useSaveCalculationMutation();
  const [deleteCalculation, { error: deleteCalculationError }] = useDeleteCalculationMutation();
  const [isNewProjectDialogOpen, setIsNewProjectDialogOpen] = useState(false);
  const [calculationToDuplicate, setCalculationToDuplicate] = useState<Calculation>();
  const [isDuplicateDetailsOpen, setIsDuplicateDetailsOpen] = useState(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [duplicateZoneDetails, { data: duplicatedZoneDetails, error: duplicateZoneDetailsError }] = useSaveZoneDetailsMutation();
  const userEmail = useAppSelector((state) => state.user.email);
  const { data: userStandard, refetch: refetchUserStandard } = useGetUserStandardTemplateQuery(userEmail);

  const response = useGetStandardsQuery(null);
  const standardOptions = response.data as Standards[];

  const { data: supportWidthOptions, error: getSupportWidthsError, isLoading: isSupportWidthLoading } = useGetSupportWidthsQuery(null);
  useEffect(() => {
    const notficationKey = 'support-widths-request-error-notificaton';
    if (getSupportWidthsError) {
      enqueueSnackbar(t('projects-page.error-message-support-widths'), {
        variant: 'error',
        key: notficationKey,
      });
    } else {
      closeSnackbar(notficationKey);
    }
  }, [enqueueSnackbar, closeSnackbar, getSupportWidthsError, t]);

  const [isStandardDialogOpen, setStandardDialogOpen] = useState(false);
  const [isCalculationDialogOpen, setCalculationDialogOpen] = useState(false);

  const handleCloseStandardDialog = () => {
    setStandardDialogOpen(false);
  };

  function onAddStandardClick() {
    setStandardDialogOpen(true);
  }

  const handleCloseCalculationDialog = () => {
    setCalculationDialogOpen(false);
  };

  function onAddCalculationClick() {
    setCalculationDialogOpen(true);
  }

  const onDuplicateCalculationClick = (calculation?: Calculation) => {
    if (calculation && project) {
      setCalculationToDuplicate(duplicateCalculationData(calculation, project.standard?.id));
      setIsDuplicateDetailsOpen(true);
    }
  };

  const handleCloseDuplicateDetailsDialog = () => {
    setIsDuplicateDetailsOpen(false);
  };

  const onDuplicateSave = async (calculationDetails: CalculationDetails) => {
    if (projectId && calculationToDuplicate) {
      const newCalculation = {
        ...calculationToDuplicate,
        details: calculationDetails,
        zoneDetails: undefined,
      } as Calculation;

      await duplicateCalculation({ calculation: newCalculation, projectId }).then(async (res) => {
        const calculation = res as { data: Calculation };
        if (calculation.data) {
          handleCloseDuplicateDetailsDialog();

          const zoneDetailsToSave = {
            zones: calculationToDuplicate?.zoneDetails?.zones.map((zoneDetails) => {
              const { id, ...details } = zoneDetails;
              return details;
            }),
          } as ZoneDetails;

          if (calculationToDuplicate.zoneDetails) {
            await duplicateZoneDetails({ zoneDetails: zoneDetailsToSave, projectId, calculationId: calculation.data.id });
          }
        }
      });
      refetch();
    }
  };

  const notficationKey = 'project-get-request-error-notificaton';
  const saveStandardNotficationKey = 'project-save-standard-error-notificaton';
  const saveCalculationNotificationKey = 'project-save-calculation-error-notification';
  const deleteCalculationNotificationKey = 'project-delete-calculation-error-notification';
  const duplicateCalculationNotificationKey = 'project-duplicate-calculation-error-notification';
  const duplicateCalculationZonesNotificationKey = 'project-duplicate-calculation-zones-error-notification';

  useEffect(() => {
    if (updatedProject) {
      setIsNewProjectDialogOpen(false);
      refetch();
    }
  }, [updatedProject, refetch]);

  useEffect(() => {
    if (getProjectError) {
      enqueueSnackbar(`${t('project-details-page.error-message-load')} ${projectId} `, {
        variant: 'error',
        key: notficationKey,
      });
    } else if (editProjectError) {
      enqueueSnackbar(`${t('project-details-page.error-message-edit')} ${projectId} `, {
        variant: 'error',
        key: notficationKey,
      });
    } else if (saveStandardError) {
      enqueueSnackbar(`${t('project-details-page.error-message-save-standard')} ${projectId} `, {
        variant: 'error',
        key: saveStandardNotficationKey,
      });
    } else if (saveCalculationError) {
      enqueueSnackbar(`${t('project-details-page.error-message-save-calculation')} ${projectId} `, {
        variant: 'error',
        key: saveCalculationNotificationKey,
      });
    } else if (deleteCalculationError) {
      enqueueSnackbar(`${t('project-details-page.error-message-delete-calculation')} ${projectId} `, {
        variant: 'error',
        key: deleteCalculationNotificationKey,
      });
    } else if (duplicateCalculationError) {
      enqueueSnackbar(`${t('project-details-page.error-message-duplicate-calculation')}`, {
        variant: 'error',
        key: duplicateCalculationNotificationKey,
      });
    } else if (duplicateZoneDetailsError && duplicatedCalculation) {
      enqueueSnackbar(`${t('project-details-page.error-message-duplicate-zones')}`, {
        variant: 'error',
        key: duplicateCalculationZonesNotificationKey,
      });
      navigate(`/project/${projectId}/calculation/${duplicatedCalculation.id}`);
    } else {
      closeSnackbar(notficationKey);
      closeSnackbar(saveStandardNotficationKey);
      closeSnackbar(saveCalculationNotificationKey);
      closeSnackbar(deleteCalculationNotificationKey);
      closeSnackbar(duplicateCalculationNotificationKey);
      closeSnackbar(duplicateCalculationZonesNotificationKey);
    }
  }, [
    enqueueSnackbar,
    closeSnackbar,
    getProjectError,
    editProjectError,
    projectId,
    t,
    saveStandardError,
    saveCalculationError,
    deleteCalculationError,
    duplicateCalculationError,
    duplicateZoneDetailsError,
    duplicatedCalculation,
    navigate,
  ]);

  useEffect(() => {
    if (savedCalculation) {
      navigate(`/project/${projectId}/calculation/${savedCalculation.id}`);
    } else if (duplicatedCalculation && !calculationToDuplicate?.zoneDetails?.zones.length) {
      navigate(`/project/${projectId}/calculation/${duplicatedCalculation.id}`);
    } else if (duplicatedCalculation && duplicatedZoneDetails) {
      navigate(`/project/${projectId}/calculation/${duplicatedCalculation.id}`);
    }
  }, [savedCalculation, duplicatedCalculation, duplicatedZoneDetails, project, navigate, projectId, calculationToDuplicate?.zoneDetails]);

  const handleEditProject = async (editedProject: NewProject) => {
    if (project !== undefined) {
      const editedProjectWithId: EditProject = { id: project.id, ...editedProject };
      await editProject(editedProjectWithId);
    }
  };

  const onStandardSave = async (standard: Standard) => {
    if (projectId) {
      await saveStandard({ standard, projectId });
    }

    handleCloseStandardDialog();
    refetch();
    refetchUserStandard();
  };

  const onCalculationSave = async (calculationDetails: CalculationDetails) => {
    if (projectId) {
      const calculation = {
        details: calculationDetails,
      } as Calculation;
      await saveCalculation({ calculation, projectId });
    }

    handleCloseCalculationDialog();
    refetch();
  };

  const onDeleteCalculationClick = async (calculationId: string) => {
    if (projectId) {
      await deleteCalculation({ projectId, calculationId });
      refetch();
    }
  };

  if (isGetProjectLoading || isSupportWidthLoading) {
    return <SkeletonPage />;
  }

  let isModifiedStandardOnCalculation = false;
  let isProjectStandardOnCalculation = false;

  if (project?.calculations) {
    for (const calculation of project?.calculations) {
      if (!isProjectStandardOnCalculation) {
        isProjectStandardOnCalculation = calculation.standard === undefined;
      }

      if (!isModifiedStandardOnCalculation) {
        isModifiedStandardOnCalculation = calculation.standard !== undefined;
      }
    }
  }

  if (!project) {
    return <CenteredComponentView>{t('project-details-page.error-message-project-not-found')}</CenteredComponentView>;
  }

  const compareStandards = (projectStandard: Standard | undefined, userStandardTemplate: UserStandard | undefined) => {
    if (!projectStandard || !userStandardTemplate) return false;

    return _.isEqual(
      _.omit(projectStandard, [
        'id',
        'createdOn',
        'modifiedOn',
        'modifiedBy',
        'createdBy',
        'annexe',
        'constructionClass',
        'deflectionLimitRoof.customized',
        'deflectionLimitWall.customized',
      ]),
      _.omit(userStandardTemplate, [
        'user',
        'id',
        'createdOn',
        'modifiedOn',
        'modifiedBy',
        'createdBy',
        'annexe',
        'constructionClass',
        'deflectionLimitRoof.customized',
        'deflectionLimitWall.customized',
      ])
    );
  };

  return (
    <AppContainer header={<Header />}>
      <AppBodyContainer data-testid="projectDashboard" style={{ paddingTop: 24 + breadcrumbHeight }}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" sx={{ pb: 2 }}>
            <Typography variant="h1">{project.name}</Typography>
            <StatusLabel size="medium" value={project.status} />
          </Stack>

          <ProjectDetailsView
            project={project}
            onEditProject={handleEditProject}
            error={editProjectError}
            isNewProjectDialogOpen={isNewProjectDialogOpen}
            setIsNewProjectDialogOpen={setIsNewProjectDialogOpen}
          />

          <ProjectStandardsDetailsView
            onAddStandardClick={onAddStandardClick}
            standard={project.standard}
            onEditStandardsDetails={() => setStandardDialogOpen(true)}
            shouldShowLabel={compareStandards(project?.standard, userStandard)}
          />
          {isStandardDialogOpen && (
            <StandardDialog
              data={project.standard}
              existingUserStandard={userStandard}
              onSave={onStandardSave}
              options={standardOptions}
              supportWidthOptions={supportWidthOptions}
              onClose={handleCloseStandardDialog}
              compareStandards={compareStandards}
              title={
                project.standard
                  ? t('project-details-page.standard-dialog.edit-standard-header')
                  : t('project-details-page.standard-dialog.add-standard-header')
              }
              displayCalculationWarning={isProjectStandardOnCalculation}
              displayModifiedStandardsWarning={isModifiedStandardOnCalculation}
            />
          )}
          {project.standard !== undefined && (
            <ProjectCalculationsDetailsView
              onAddCalculationClick={onAddCalculationClick}
              onDeleteCalculationClick={onDeleteCalculationClick}
              onDuplicateCalculationClick={onDuplicateCalculationClick}
              project={project}
            />
          )}
          {isCalculationDialogOpen && (
            <CalculationDialog onSave={onCalculationSave} onClose={handleCloseCalculationDialog} mode={CalculationDialogMode.Add} />
          )}
          {isDuplicateDetailsOpen && (
            <CalculationDialog
              onSave={onDuplicateSave}
              onClose={handleCloseDuplicateDetailsDialog}
              calculation={calculationToDuplicate}
              mode={CalculationDialogMode.Duplicate}
            />
          )}
        </Stack>
      </AppBodyContainer>
    </AppContainer>
  );
}

export default ProjectPage;
